import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Grow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Dialog,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Play,
  Pause,
  ChevronDown,
  ArrowRight,
  FileText,
} from 'lucide-mui';
import {
  UploadState,
  FeedbackSnackbar,
  useIframeResizer,
  GEMINI_MODEL,
  uploadToScreenApp,
  ProgressModal,
} from './common';
import { config } from '../../../config';

interface Chapter {
  title: string;
  timestamp: string;
  description: string;
  seconds: number;
}

interface Summary {
  chapters: Chapter[];
}

const parseTimestamp = (timestamp: string): number => {
  const parts = timestamp.split(':').reverse();
  let seconds = 0;
  for (let i = 0; i < parts.length; i += 1) {
    seconds += parseInt(parts[i], 10) * 60 ** i;
  }
  return seconds;
};

const AudioSummarizer: React.FC = () => {
  const { t } = useTranslation();
  useIframeResizer();

  const [state, setState] = useState<UploadState>({
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLargeFile, setIsLargeFile] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioBlob = useRef<Blob | null>(null);

  const MAX_GEMINI_SIZE = 20 * 1024 * 1024; // 20MB in bytes

  const seekToTime = (seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = seconds;
      if (!isPlaying) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const analyzeSummary = async (blob: Blob) => {
    try {
      console.log('Starting audio analysis...', {
        fileType: blob.type,
        fileSize: blob.size,
      });
      setState((prev) => ({
        ...prev,
        uploading: true,
      }));

      const base64Audio = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(blob);
      });

      console.log('Sending request to Gemini API...');

      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: GEMINI_MODEL,
          contents: [
            {
              parts: [
                {
                  text: `You are ScreenApp's professional audio analyzer. Break down this audio into detailed chapters. For each chapter:
1. Start with a timestamp in [MM:SS] format
2. Give a clear, concise title that captures the main topic
3. Provide a detailed description of what's discussed
4. Ensure chapters are sequential and cover the entire audio
5. Start the first chapter at [00:00]
6. Make chapters roughly 30-60 seconds each

Format your response exactly like this:

## [00:00] Introduction and Context
Detailed description of the opening segment, explaining the context and initial points raised.

## [01:30] Key Discussion Points
Thorough breakdown of the main topics covered in this segment, including any important quotes or details.

[Continue with more chapters, maintaining this exact format]`,
                },
                {
                  inlineData: {
                    mimeType: 'audio/webm',
                    data: base64Audio,
                  },
                },
              ],
            },
          ],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(
          `Failed to analyze audio: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      const result = data.candidates[0].content.parts[0].text;
      console.log('Extracted result text:', result);

      // Parse chapters
      const chapterRegex = /##\s*\[(\d{2}:\d{2})\]\s*([^\n]+)\n([\s\S]*?)(?=##|$)/g;
      const chapters: Chapter[] = [];
      const matches = [...result.matchAll(chapterRegex)];

      matches.forEach((match) => {
        const [, timestamp, title, description] = match;
        chapters.push({
          timestamp,
          title: title.trim(),
          description: description.trim(),
          seconds: parseTimestamp(timestamp),
        });
      });

      console.log('Parsed chapters:', chapters);
      setSummary({ chapters });

      setState((prev) => ({
        ...prev,
        uploading: false,
        uploadComplete: true,
      }));
    } catch (error) {
      console.error('Error in analyzeSummary:', error);
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to analyze audio. Please try again.',
        uploading: false,
      }));
    }
  };

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith('audio/')) {
      setState((prev) => ({
        ...prev,
        error: 'Please upload an audio file.',
      }));
      return;
    }

    audioBlob.current = file;
    const url = URL.createObjectURL(file);
    setAudioUrl(url);

    if (file.size > MAX_GEMINI_SIZE) {
      console.log('File too large for Gemini analysis, uploading directly...', {
        fileSize: file.size,
        maxSize: MAX_GEMINI_SIZE,
      });
      setIsLargeFile(true);
      uploadToScreenApp(
        file,
        file.name,
        () => setState((prev) => ({ ...prev, uploading: true })),
        (progress) => setState((prev) => ({ ...prev, progress })),
        () => setState((prev) => ({ ...prev, uploadComplete: true })),
        (error) => setState((prev) => ({ ...prev, error }))
      );
    } else {
      setIsLargeFile(false);
      analyzeSummary(file);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ p: 3, maxWidth: '60rem', width: '100%' }}>
        {!summary && (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              borderStyle: 'dashed',
              borderWidth: 3,
              borderRadius: '12px',
              bgcolor: isDragging ? 'action.hover' : 'background.paper',
              borderColor: isDragging ? 'primary.main' : 'secondary.dark',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              },
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              accept="audio/*,.m4a,audio/m4a,audio/x-m4a"
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <UploadIcon
                sx={{
                  fontSize: 56,
                  color: isDragging ? 'primary.main' : 'text.secondary',
                  transition: 'all 0.2s ease-in-out',
                  transform: isDragging ? 'scale(1.1)' : 'scale(1)',
                }}
              />

              <Typography variant="h5" fontWeight="medium" sx={{ mb: 1 }}>
                {t('audiosummarizer.title')}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {t('audiosummarizer.subtitle')}
              </Typography>
            </Box>
          </Paper>
        )}

        {summary && (
          <Grow in={!!summary} timeout={500}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  mb: 3,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (audioRef.current) {
                      if (isPlaying) {
                        audioRef.current.pause();
                      } else {
                        audioRef.current.play();
                      }
                      setIsPlaying(!isPlaying);
                    }
                  }}
                  startIcon={isPlaying ? <Pause /> : <Play />}
                  sx={{
                    borderRadius: 28,
                    minWidth: 120,
                  }}
                >
                  {isPlaying
                    ? t('audiosummarizer.buttons.pause')
                    : t('audiosummarizer.buttons.play')}
                </Button>
                <audio
                  ref={audioRef}
                  src={audioUrl || undefined}
                  onEnded={() => setIsPlaying(false)}
                  onTimeUpdate={handleTimeUpdate}
                  style={{ display: 'none' }}
                >
                  <track kind="captions" src="" label="Audio preview" default />
                </audio>
              </Box>

              <Stack spacing={0}>
                {summary.chapters.map((chapter, index) => {
                  const isAvailable =
                    index < Math.ceil(summary.chapters.length / 2);
                  const nextChapter = summary.chapters[index + 1];

                  return (
                    <Accordion
                      key={index}
                      expanded={isAvailable}
                      onChange={() => {
                        if (!isAvailable) {
                          // If clicking on a locked chapter, trigger upload
                          if (audioBlob.current) {
                            setIsLargeFile(true);
                            uploadToScreenApp(
                              audioBlob.current,
                              'audio-recording.webm',
                              () =>
                                setState((prev) => ({
                                  ...prev,
                                  uploading: true,
                                  uploadComplete: false, // Reset complete state
                                })),
                              (progress) =>
                                setState((prev) => ({ ...prev, progress })),
                              () =>
                                setState((prev) => ({
                                  ...prev,
                                  uploadComplete: true,
                                })),
                              (error) =>
                                setState((prev) => ({ ...prev, error }))
                            );
                          }
                        }
                      }}
                      disableGutters
                      sx={{
                        '&:before': { display: 'none' },
                        boxShadow: 'none',
                        borderRadius: 0,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'transparent',
                        '&:first-of-type': {
                          borderTopLeftRadius: 0,
                          borderTopRightRadius: 0,
                        },
                        '&.Mui-expanded': {
                          margin: 0,
                        },
                        '& .MuiAccordionSummary-root': {
                          backgroundColor:
                            currentTime >= chapter.seconds &&
                            (!nextChapter || currentTime < nextChapter.seconds)
                              ? 'secondary.light'
                              : 'background.default',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          opacity: !isAvailable ? 0.7 : 1,
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          isAvailable ? <ChevronDown /> : <ArrowRight />
                        }
                        sx={{
                          minHeight: 'unset',
                          padding: (theme) => theme.spacing(1, 1.5),
                          '& .MuiAccordionSummary-content': {
                            margin: '8px 0',
                            '&.Mui-expanded': {
                              margin: '8px 0',
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 500,
                              flex: 1,
                              color: !isAvailable
                                ? 'text.disabled'
                                : 'text.primary',
                            }}
                          >
                            {!isAvailable
                              ? t('audiosummarizer.unlockMessage')
                              : chapter.title}
                          </Typography>
                          {isAvailable && (
                            <Chip
                              icon={
                                <Play
                                  style={{
                                    width: 16,
                                    height: 16,
                                    marginLeft: 4,
                                  }}
                                />
                              }
                              label={chapter.timestamp}
                              onClick={(e) => {
                                e.stopPropagation();
                                seekToTime(chapter.seconds);
                              }}
                              clickable
                              sx={{
                                backgroundColor: 'background.paper',
                                color: 'primary.main',
                                height: '28px',
                                '&:hover': {
                                  backgroundColor: 'background.paper',
                                  '& .MuiChip-icon': {
                                    color: 'primary.main',
                                  },
                                },
                                '& .MuiChip-icon': {
                                  color: 'primary.main',
                                  marginRight: '-4px',
                                },
                              }}
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      {isAvailable && (
                        <AccordionDetails
                          sx={{
                            pt: 1,
                            pb: 2,
                            px: 1.5,
                            backgroundColor: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.03)'
                                : 'rgba(0, 0, 0, 0.02)',
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'pre-wrap' }}
                          >
                            {chapter.description}
                          </Typography>
                        </AccordionDetails>
                      )}
                    </Accordion>
                  );
                })}
              </Stack>

              {summary.chapters.length >
                Math.ceil(summary.chapters.length / 2) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      if (audioBlob.current) {
                        setIsLargeFile(true);
                        uploadToScreenApp(
                          audioBlob.current,
                          'audio-recording.webm',
                          () =>
                            setState((prev) => ({
                              ...prev,
                              uploading: true,
                              uploadComplete: false, // Reset complete state
                            })),
                          (progress) =>
                            setState((prev) => ({ ...prev, progress })),
                          () =>
                            setState((prev) => ({
                              ...prev,
                              uploadComplete: true,
                            })),
                          (error) => setState((prev) => ({ ...prev, error }))
                        );
                      }
                    }}
                    startIcon={<FileText />}
                    sx={{
                      borderRadius: 28,
                      px: 4,
                      py: 1,
                    }}
                  >
                    {t('audiosummarizer.buttons.uploadToUnlock')}
                  </Button>
                </Box>
              )}
            </Box>
          </Grow>
        )}

        <Dialog
          open={state.uploading && !isLargeFile}
          PaperProps={{
            sx: {
              backgroundColor: 'background.paper',
              boxShadow: 'none',
              minWidth: '300px',
              p: 3,
              textAlign: 'center',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress
              size={48}
              thickness={4}
              sx={{
                color: 'primary.main',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                },
              }}
            />
            <Typography variant="body1" color="text.secondary">
              {t('audiosummarizer.processing')}
            </Typography>
          </Box>
        </Dialog>

        <ProgressModal
          open={state.uploading && isLargeFile}
          progress={state.progress}
          isComplete={state.uploadComplete}
          processingMessage={t('audiosummarizer.uploadingLargeFile')}
          completeMessage={t('audiosummarizer.uploadComplete')}
        />

        <FeedbackSnackbar
          open={!!state.error}
          message={state.error || t('audiosummarizer.error')}
          severity="error"
          onClose={() => setState((prev) => ({ ...prev, error: null }))}
        />
      </Box>
    </Box>
  );
};

export default AudioSummarizer;
